import React from "react";
import { Warning } from "@phosphor-icons/react";
import { formatIdDate, formatIdDateTime } from "@/lib/format";

// Small label/value row used inside the two info cards.
function Row({ label, children }) {
  return (
    <div className="flex justify-between border-b border-dashed border-border pb-2">
      <dt className="overline">{label}</dt>
      <dd className="font-semibold font-mono text-right">{children}</dd>
    </div>
  );
}

/**
 * Read-only data grid for the SPB detail page:
 *   • Informasi Pengiriman (tanggal, slot, distrik, plant, SO, pelanggan…)
 *   • Muatan & Truk (produk, jumlah, jumlah termuat, plat, supir)
 *   • Realisasi (only rendered when Excel upload has synced this SPB)
 *
 * Extracted from `SPBDetail.jsx` Feb 2026 to slim down the orchestrator.
 * Purely presentational — no state, no fetching.
 */
export default function SPBContent({ spb }) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="border border-border p-6 bg-card">
          <div className="overline mb-4">Informasi Pengiriman</div>
          <dl className="space-y-3 text-sm">
            <Row label="Tanggal">{formatIdDate(spb.tanggal)}</Row>
            <Row label="Slot Antrian">
              {spb.slot_antrian ? (
                <span className="font-mono font-bold bg-primary/10 text-primary border border-primary/40 px-2 py-0.5">
                  {spb.slot_antrian}
                </span>
              ) : "-"}
            </Row>
            <Row label="Distrik">{spb.distrik_kode} ({spb.distrik_nama})</Row>
            <Row label="Plant">{spb.plant || "-"}</Row>
            <Row label="Tipe Pengiriman">{spb.tipe_pengiriman || "-"}</Row>
            <Row label="No SO">
              {Array.isArray(spb.so_allocations) && spb.so_allocations.length > 0 ? (
                <div className="whitespace-pre-line font-mono text-xs" data-testid="detail-so-allocations">
                  {spb.so_allocations.map((a) => `${a.no_so}: ${a.qty}`).join("\n")}
                </div>
              ) : (spb.no_so || "-")}
            </Row>
            <Row label="Nama Pelanggan">
              <span data-testid="detail-nama-pelanggan">{spb.nama_pelanggan || "-"}</span>
              {(spb.pelanggans || []).length > 1 && (
                <span className="ml-2 inline-block border border-blue-400/60 bg-blue-50 text-blue-800 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5">
                  Multi-drop {spb.pelanggans.length}×
                </span>
              )}
            </Row>
            <Row label="Alamat Pengiriman">
              <span data-testid="detail-alamat-pengiriman" className="whitespace-pre-line">
                {spb.alamat_pengiriman || "-"}
              </span>
            </Row>
            {(spb.pelanggans || []).length > 1 && (
              <Row label="Tujuan Tambahan">
                <div className="space-y-2" data-testid="detail-extra-pelanggans">
                  {spb.pelanggans.slice(1).map((p, i) => (
                    <div key={`extra-${p.nama}-${i}`} className="border-l-2 border-blue-400 pl-3 py-1">
                      <div className="font-semibold text-sm">#{i + 2}. {p.nama}</div>
                      <div className="text-xs text-muted-foreground whitespace-pre-line">{p.alamat}</div>
                    </div>
                  ))}
                </div>
              </Row>
            )}
            <Row label="Admin Pembuat">{spb.admin_nama}</Row>
            {spb.keterangan && (
              <Row label="Keterangan">
                <span data-testid="detail-keterangan" className="whitespace-pre-line font-sans">
                  {spb.keterangan}
                </span>
              </Row>
            )}
          </dl>
        </div>
        <div className="border border-border p-6 bg-card">
          <div className="overline mb-4">Muatan &amp; Truk</div>
          <dl className="space-y-3 text-sm">
            <Row label="Produk">{spb.produk_nama}</Row>
            <Row label="Nama Barang">{spb.nama_barang}</Row>
            <Row label="Jumlah">{spb.jumlah} {spb.satuan}</Row>
            <Row label="Jumlah Termuat">
              {spb.jumlah_termuat != null && spb.jumlah_termuat !== "" ? (
                <span
                  data-testid="detail-jumlah-termuat"
                  className={Number(spb.jumlah_termuat) !== Number(spb.jumlah) ? "text-orange-600 font-bold" : "font-mono"}
                >
                  {spb.jumlah_termuat} {spb.satuan}
                </span>
              ) : (<span className="text-muted-foreground">Belum diinput</span>)}
            </Row>
            <Row label="No Polisi">{spb.no_polisi}</Row>
            <Row label="Supir">{spb.nama_supir || "-"}</Row>
            <Row label="Transporter">{spb.transporter || "-"}</Row>
          </dl>
        </div>
      </div>

      {spb.realisasi && (
        <div className="border border-border p-6 bg-card mb-8 no-print">
          <div className="overline mb-4">Realisasi (Upload Excel)</div>
          <dl className="space-y-2 text-sm">
            <Row label="No Polisi (real)">{spb.realisasi.no_polisi || "-"}</Row>
            <Row label="Jumlah (real)">{spb.realisasi.jumlah}</Row>
            <Row label="Nama Barang (real)">{spb.realisasi.nama_barang}</Row>
            <Row label="Sinkron Terakhir">{formatIdDateTime(spb.realisasi.synced_at)}</Row>
          </dl>
          {spb.realisasi.mismatch_reasons?.length > 0 && (
            <div className="mt-4 p-3 border border-destructive bg-destructive/5 text-sm">
              <div className="flex items-center gap-2 font-semibold mb-1 text-destructive">
                <Warning size={14} weight="fill" /> Ketidakcocokan
              </div>
              <ul className="list-disc list-inside">
                {spb.realisasi.mismatch_reasons.map((r, i) => (<li key={`${i}-${r}`}>{r}</li>))}
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  );
}
